scalar BigInt

# Case Ledgers
#enums
enum LedgerSourceType {
  CLIENT
  LENDER
  LAND_REGISTRY
  CLUB
  NETWORK
  MASTER_BROKER
  PACKAGER
  INTRODUCER
  ADVISOR
  INSURER
  USER
  CONVEYANCER
}

enum LedgerSourceHydrationType {
  ORGANISATION
  CLIENT
  USER
}

enum LedgerDirection {
  DEBIT
  CREDIT
}

enum LedgerStatus {
  SETTLED
  DUE
  WRITTEN_OFF
  PARTIALLY_PAID
  PARTIALLY_WRITTEN_OFF
}

enum LedgerRecurringPaymentsStatus {
  IN_FORCE
  OVERDUE
  CLOSE
  LIVE
  CANCELLED
}

enum PaymentCancelationStatus {
  LIVE
  CANCELLED
  AWAITING
  FAILED
}

enum PaymentProvider {
  GOCARDLESS
}

enum LedgerOrderBy {
  ASC
  DESC
}

enum LedgerType {
  PRE_MORTGAGE_ADVICE_FIXED
  PRE_MORTGAGE_ADVICE_PERCENTAGE
  PRE_MORTGAGE_ADVICE_COMBINED
  PRE_MORTGAGE_RECOMMENDATION_FIXED
  PRE_MORTGAGE_RECOMMENDATION_PERCENTAGE
  PRE_MORTGAGE_RECOMMENDATION_COMBINED
  PRE_MORTGAGE_APPLICATION_FIXED
  PRE_MORTGAGE_APPLICATION_PERCENTAGE
  PRE_MORTGAGE_APPLICATION_COMBINED
  MORTGAGE_OFFER_FIXED
  MORTGAGE_OFFER_PERCENTAGE
  MORTGAGE_OFFER_COMBINED
  MORTGAGE_LEGAL_COMPLETION_FIXED
  MORTGAGE_LEGAL_COMPLETION_PERCENTAGE
  MORTGAGE_LEGAL_COMPLETION_COMBINED
  MORTGAGE_PROC_FEE
  PROTECTION_PROC_FEE
  GENERAL_INSURANCE_PROC_FEE
  LAND_REGISTRY_REPORT_FEE
  INTRODUCER_PROTECTION_COMMISSION
  ADVISOR_PROTECTION_COMMISSION
  INTRODUCER_MORTGAGE_COMMISSION
  ADVISOR_MORTGAGE_COMMISSION
  CLAWBACK
  MORTGAGE_REFERENCE_FEE
  VALUATION_FEE
  RELATED_USER_COMMISSION
  CONVEYANCING_REFERRAL_FEE
  OTHER_REFERRAL_FEE
  PENSION_REFERRAL_FEE
  WILL_REFERRAL_FEE
  MASTER_BROKER_REFERRAL_FEE
  CLUB_FEE
  CASE_MANAGER_COMMISSION
  REFERRER_COMMISSION
  ADMINISTRATOR_COMMISSION
  LEAD_QUALIFIER_COMMISSION
  WATCHER_COMMISSION
  REVIEWER_COMMISSION
  ADVISOR_PROTECTION_TIER_COMMISSION
  ADVISOR_MORTGAGE_TIER_COMMISSION
  MORTGAGE_LEGAL_EXCHANGE_FIXED
  MORTGAGE_LEGAL_EXCHANGE_PERCENTAGE
  MORTGAGE_LEGAL_EXCHANGE_COMBINED
  INTRODUCER_GENERAL_INSURANCE_COMMISSION
  ADVISOR_GENERAL_INSURANCE_COMMISSION
  ADVISOR_CONVEYANCING_COMMISSION
}

enum PaymentDirection {
  CREDIT
  DEBIT
}

enum LedgerPaymentFrequency {
  MONTHLY
}

type CaseLedgers {
  recurringLedgers: [RecurringLedger!]
  oneOffLedgers: [OneOffLedger!]
}

type Ledger implements LedgerBase {
  id: Int!
  advisorId: String
  advisor: User
  caseId: String
  clientIds: [String!]
  clients: [ClientVersion!]
  clubId: String
  club: Organisation
  propertyId: String
  period_id: String
  protection_policy_number: String
  protection_quote_reference: String
  mortgageAccountNumber: String
  sourceId: LedgerPaymentSourceId
  property: PropertyVersion
  type: LedgerType
  isOverdue: Boolean
  dueDate: String
  settledDate: String
  payments: [LedgerPayment!]
  status: LedgerStatus
  amountDue: String
  grossFee: String
  originFeeAmount: String
  isTier: Boolean
  tierFrom: BigInt
  tierTo: BigInt
  originLedgerId: Int
  organisationID: String
  created_by: String
}

type OneOffLedger implements LedgerBase {
  id: Int!
  advisorId: String
  advisor: User
  caseId: String
  clientIds: [String!]
  clients: [ClientVersion!]
  clubId: String
  club: Organisation
  propertyId: String
  sourceId: LedgerPaymentSourceId
  property: PropertyVersion
  protection_policy_number: String
  protection_quote_reference: String
  type: LedgerType
  isOverdue: Boolean
  status: LedgerStatus
  amountDue: Int
  grossFee: Int
  payments: [LedgerPayment!]
  dueDate: String
  settledDate: String
  isWrittenOff: Boolean
  paymentDirection: LedgerDirection
  orgId: ID
  isTier: Boolean
  product_source_id: String
  originLedgerId: Int
}

type RecurringLedger implements LedgerBase {
  id: Int!
  advisorId: String
  advisor: User
  caseId: String
  clientIds: [String!]
  clients: [ClientVersion!]
  clubId: String
  club: Organisation
  propertyId: String
  sourceId: LedgerPaymentSourceId
  property: PropertyVersion
  type: LedgerType
  isOverdue: Boolean
  status: LedgerRecurringPaymentsStatus
  amount: Int
  startDate: String
  endDate: String
  orgId: ID
  nextPayment: String
  paymentFrequency: LedgerPaymentFrequency
  recurringAmount: Int
}

#Ledger Filter
type LedgerPaymentFiltersSourceId {
  id: ID!
  hydrationType: LedgerSourceHydrationType!
  sourceType: LedgerSourceType!
  details: LedgerPaymentSourceDetail
}

union LedgerPaymentSourceDetail = User | Organisation | ClientVersion

type LedgerFilters {
  advisorIds: [LedgerPaymentFiltersSourceId!]
  advisors: [User!]
  clubIds: [LedgerPaymentFiltersSourceId!]
  clubs: [Organisation!]
  sourceIds: [LedgerPaymentFiltersSourceId!]
  periodIds: [String!]
  period_ids: [String!]
}

#Get Ledgers
type LedgerPaymentSourceId {
  id: ID!
  hydrationType: LedgerSourceHydrationType!
  sourceType: LedgerSourceType
  details: LedgerPaymentSourceDetail
}

type LedgerFilter {
  orderByAmountDue: LedgerOrderBy
  orderByDueDate: LedgerOrderBy
  orderBySettledDate: LedgerOrderBy
  dueDateStart: String
  dueDateEnd: String
  isOverdue: Boolean
  statuses: [LedgerStatus!]
  sourceIds: [String!]
  clubIds: [String!]
  advisorIds: [String!]
  settledDateStart: String
  settledDateEnd: String
  paymentDirections: [PaymentDirection]
  regulated: Boolean
  includeTotals: Boolean
  regulated_by: ID
  period_ids: [String]
  periodIds: [String]
  periodStartDate: String
  periodEndDate: String
  ledgerTypes: [LedgerType]
  commissionsOnlyForAdvisor: Boolean
}

input LedgerFilterInput {
  orderByAmountDue: LedgerOrderBy
  orderByDueDate: LedgerOrderBy
  orderBySettledDate: LedgerOrderBy
  dueDateStart: String
  dueDateEnd: String
  isOverdue: Boolean
  statuses: [LedgerStatus!]
  sourceIds: [String!]
  clubIds: [String!]
  advisorIds: [String!]
  periodIds: [String!]
  periodStartDate: String
  periodEndDate: String
  # Only add orgIds to the LedgerFilterInput because
  # it is not returned from the filter object by BE
  orgIds: [String!]
  settledDateStart: String
  settledDateEnd: String
  paymentDirections: [PaymentDirection]
  regulated: Boolean
  includeTotals: Boolean
  regulated_by: ID
  ledgerTypes: [LedgerType]
  commissionsOnlyForAdvisor: Boolean
  groupTierCommissions: Boolean
}

input GetLedgersInput {
  filter: LedgerFilterInput
  pageSize: Int
  page_size: Int
  bookmark: String
  user_id: String
}

input GetLedgerFiltersInput {
  org_ids: [String!]
  user_id: String
  ledger_type: [LedgerType!]
  has_outstanding_commissions: Boolean
  period_id: [String!]
  period_start_date: String
  period_end_date: String
  include_clawback_advisor_in_source: Boolean
}

type GetLedgersResponse {
  filter: LedgerFilter
  ledgers: [Ledger!]
  nextPage: LedgerNextPage
  totals: LedgerTotals
}

type ProviderStatement {
  id: ID!
  provider_id: String
  provider_name: String
  club_name: String
  period_id: String
  total_amount: Float
  num_transactions: Int
  items_to_match: Int
  status: String
}

input ProviderStatementFilterInput {
  provider_ids: [String!]
  club_ids: [String!]
  statuses: [String!]
  period_ids: [String!]
  sort_by_provider: String
  sort_by_amount: String
  sort_by_number_of_transactions: String
  sort_by_number_of_items_to_match: String
}

type ProviderStatementFilters {
  providerIds: [String!]
  providers: [Organisation!]
  clubIds: [String!]
  clubs: [Organisation!]
}

input GetProviderStatementFiltersInput {
  provider_ids: [String!]
  club_ids: [String!]
  period_ids: [String!]
  statuses: [String!]
}

input GetProviderStatementsInput {
  filter: ProviderStatementFilterInput
  pageSize: Int
  bookmark: String
}

type GetProviderStatementsResponse {
  statements: [ProviderStatement!]!
  nextPage: LedgerNextPage
}

enum StatementType {
  MORTGAGE_PROVIDER_STATEMENT
  PROTECTION_PROVIDER_STATEMENT
}

input UploadProviderStatementInput {
  statement_type: StatementType!
  validation_total: Float!
  club_id: ID
  provider_id: ID
  period_id: ID
  document_name: String!
  data_base64: String!
}

type UploadProviderStatementResponse {
  statement_id: ID
}

input DownloadProviderStatementTemplateInput {
  statement_type: StatementType!
}

type ProviderStatementTemplate {
  filename: String!
  content_base64: String!
}

type LedgerSummaryFilter {
  # used as query parameters
  totals_only: Boolean
  include_overall_totals: Boolean
  group_by_period: Boolean
  org_id: [String!]
  user_id: [String!]
  if_due: Boolean
  # used as input parameters
  statuses: [LedgerStatus!]
  club_ids: [String!]
  due_date_start: String
  due_date_end: String
  settled_date_start: String
  settled_date_end: String
  period_end_date: String
  period_start_date: String
  order_by_commissions: LedgerOrderBy
  order_by_gross_revenue: LedgerOrderBy
  order_by_net_commission: LedgerOrderBy
  order_by_payee: LedgerOrderBy
  period_ids: [String!]
  source_ids: [String!]
  advisor_ids: [String!]
  period_start: String
  period_end: String
}

input LedgerSummaryFilterInput {
  # used as query parameters
  totals_only: Boolean
  include_overall_totals: Boolean
  group_by_period: Boolean
  org_id: [String!]
  user_id: [String!]
  if_due: Boolean
  # used as input parameters
  statuses: [LedgerStatus!]
  club_ids: [String!]
  due_date_start: String
  due_date_end: String
  settled_date_start: String
  settled_date_end: String
  period_end_date: String
  period_start_date: String
  order_by_commissions: LedgerOrderBy
  order_by_gross_revenue: LedgerOrderBy
  order_by_net_commission: LedgerOrderBy
  order_by_payee: LedgerOrderBy
  period_ids: [String!]
  source_ids: [String!]
  advisor_ids: [String!]
  period_start: String
  period_end: String
  payment_directions: [PaymentDirection]
}

input GetLedgerSummaryInput {
  filter: LedgerSummaryFilterInput
  page_size: Int
  bookmark: String
  user_id: String
}

type GetLedgerSummaryResponse {
  ledgers_summary: [LedgerSummary]
  filter: LedgerSummaryFilter
  nextPage: LedgerNextPage
  totals: LedgerSummaryTotals
}

type TierSummaryDetails {
  commissions: Int
  net_commission: BigInt
  paid_net_commission: BigInt
  tier_from: BigInt
  tier_to: BigInt
}

type TierSummary {
  mortgage: [TierSummaryDetails]
  protection: [TierSummaryDetails]
}

type LedgerSummary {
  advisor_id: String
  payee: String
  role: String
  commission_ledger_ids_cache_key: String
  commissions_total: Int
  clawback_total: BigInt
  gross_revenue_total: BigInt
  net_commission_total: BigInt
  conveyancing_summary: LedgerSummaryDetails
  gi_summary: LedgerSummaryDetails
  mortgage_summary: LedgerSummaryDetails
  protection_summary: LedgerSummaryDetails
  tier_summary: TierSummary
  paid_commissions_total: Int
  paid_gross_revenue_total: BigInt
  paid_net_commission_total: BigInt
  period_end: String
  period_id: String
  period_name: String
  period_start: String
  clawbacks: Int
  paid_clawback_total: BigInt
}

type LedgerSummaryDetails {
  clawback: BigInt
  commissions: Int
  gross_revenue: BigInt
  net_commission: BigInt
  paid_commissions: Int
  paid_gross_revenue: BigInt
  paid_net_commission: BigInt
}

type LedgerPayment {
  amount: BigInt
  date: String
  payee: LedgerPaymentSourceId
  id: ID!
  status: PaymentCancelationStatus
  provider: PaymentProvider
  external_id: String
  redirect_uri: String
}

type LedgerNextPage {
  bookmark: String
  pageSize: Int
  page_size: Int
}

type LedgerTotals {
  amount_due: BigInt
  amount_paid: BigInt
  number_of_payments: Int
  number_of_ledgers: Int
}

type LedgerSummaryTotals {
  commissions: BigInt
  gross_fee: BigInt
  net_commission: BigInt
  paid_commissions: BigInt
  paid_gross_fee: BigInt
  paid_net_commission: BigInt
}

#put ledger(s) full payment
input PutLedgersFullPaymentInput {
  ledgerCacheKey: String
  ledgerIds: [Int!]
  newAmount: Int
  settledDate: String!
}

type LedgerFullPayment {
  amount: Int
  created_at: String
  created_by: String
  edited_at: String
  edited_by: String
  external_id: String
  id: Int
  ledger_id: Int
  processor: String
  received_date: String
  redirect_uri: String
  status: LedgerRecurringPaymentsStatus
}

type PutLedgersFullPaymentResponse {
  payments: [LedgerFullPayment]
}

#patch ledger
input UpdateLedgerInput {
  writtenOff: Boolean
  advisorId: ID
  amountDue: Int
  caseId: ID
  clientIds: [ID!]
  clubId: ID
  counterPartyId: ID
  counterPartyType: String
  dueDate: String
  grossFee: Int
  orgId: ID
  paymentDirection: PaymentDirection
  propertyId: ID
  type: LedgerType
  status: LedgerRecurringPaymentsStatus
  payerName: String
  isTier: Boolean
  protection_quote_reference: String
  protection_policy_number: String
  productSourceId: ID
  periodId: ID
  settledDate: String
  originLedgerId: Int
}

input UpdateRecurringLedgerInput {
  writtenOff: Boolean
  advisorId: ID
  recurringAmount: Int
  caseId: ID
  clientIds: [ID!]
  clubId: ID
  counterPartyId: ID
  counterPartyType: String
  endDate: String
  startDate: String
  orgId: ID
  propertyId: ID
  type: LedgerType
  status: LedgerRecurringPaymentsStatus
  paymentFrequency: LedgerPaymentFrequency
  paymentDirection: PaymentDirection
  payerName: String
}

#put ledger payment
input EditLedgerPaymentInput {
  amount: Int!
  receivedDate: String
}

input CreateLedgerInput {
  advisorId: ID
  amountDue: Int!
  caseId: ID!
  clientIds: [ID!]
  clubId: ID
  counterPartyId: ID
  counterPartyType: String!
  dueDate: String!
  grossFee: Int
  orgId: ID!
  paymentDirection: PaymentDirection!
  propertyId: ID
  type: LedgerType!
  isTier: Boolean
  productSourceId: ID
  originLedgerId: Int
}

input CreateRecurringPaymentInput {
  advisorId: ID
  caseId: ID!
  clientIds: [ID!]
  clubId: ID
  counterPartyId: ID
  counterPartyType: String!
  grossFee: Int
  orgId: ID!
  paymentDirection: PaymentDirection!
  propertyId: ID
  type: LedgerType!
  status: LedgerRecurringPaymentsStatus
  amount: Int
  startDate: String
  endDate: String
  paymentFrequency: LedgerPaymentFrequency
  recurringAmount: Int
}

input LedgerPaymentInput {
  status: PaymentCancelationStatus
}

input InitiateGoCardlessPaymentInput {
  clientIds: [ID!]!
  ledgerID: ID
}

type GoCardlessURLResponse {
  url: String
}

input GoCardlessAuthCallbackInput {
  code: ID
  state: String
}

type GetPaymentsResponse {
  payments: [AwaitedPayment!]
}

type AwaitedPayment {
  id: ID
  url: String
  amount: Int
  case_id: String
  org_id: String

  # Extended fields so we can access the various entities via queries
  organisation(useClientApi: Boolean): Organisation
}

type GoCardlessStatusResponse {
  status: String
}

type GetNeedsMandateResponse {
  status: String!
}

type CreateMandateResponse {
  status: String!
  url: String
}
